// Extra tokens the Field Admin design mockups (NeuraDesigns/field-admin-app/*.html) use
// that Global/colors.js doesn't define. Import both — colors.js stays the source for
// BG/CARD_BG/BORDER/AMBER/LABEL, this only adds what's missing.
export const FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
export const MONO_FONT_FAMILY = "'SF Mono', 'Menlo', monospace";

export const OK_GREEN = "#22C55E";
export const OK_GREEN_TEXT = "#4ADE80";
export const WARN_YELLOW = "#FBBF24";
export const BAD_RED = "#EF4444";
export const BAD_RED_TEXT = "#F87171";
// neura.css .row-actions-item.danger is #FCA5A5, lighter than the confirm button's
// solid red. Applied through FA_MENU_PAPER_SX's data-danger hook, not a MenuItem sx.
export const FA_DANGER_MENU_COLOR = "#FCA5A5";

export const BORDER_STRONG = "rgba(255,255,255,0.12)";

// FieldAdminBottomNav's height, set explicitly on the nav itself so this is authoritative
// rather than a measurement that can drift. The H&S step footer is fixed directly above
// the nav and offsets by exactly this, so anything that changed the nav's height silently
// would otherwise leave the two overlapping.
export const FA_BOTTOM_NAV_HEIGHT = "69px";

// The design's low-reading thresholds (hs-devices.html's saveReadings() script, and
// devices.html .tbl-cell .warn on the register's reading cell). Shared because the daily
// capture screen and the register are
// two views of the same reading: when they disagreed (25/200 vs 20/100) the same device
// showed "Battery low" on one and clean on the other. Inclusive, per the design's <=.
export const LOW_BATTERY_PERCENT = 20;
export const LOW_DATA_MB = 100;

// Every payload carrying a team_id also carries its team_name, but the name is only as
// present as its admin-set Team row — so the numeric form stays as the fallback rather
// than rendering a blank or an invented label.
export function teamLabel(name, id) {
  if (name) return name;
  return id == null ? "—" : `Team #${id}`;
}

// Same contract for installers. The roster is company-scoped and StockMovement.installer_id
// is a typed-in integer rather than a verified FK, so an id the roster doesn't carry keeps
// the numeric form instead of rendering blank.
export function installerLabel(name, id) {
  return name || `Installer #${id}`;
}

// ── Shared formatting ────────────────────────────────────────────────────────────────
// These lived as per-page copies and drifted, which is the whole argument for hoisting
// them: the same value rendered differently depending on which screen you were looking at.

/** Parse a bare Django DateField string ("2026-08-12") in the LOCAL timezone.
 *
 * `new Date("2026-08-12")` is parsed as UTC midnight per spec, which renders as the
 * previous day at any negative UTC offset. Appending a time forces local parsing. */
export function parseLocalDate(iso) {
  return new Date(`${iso}T00:00:00`);
}

/** Initials for an avatar — two letters.
 *
 * QE oversight used three, so the same person showed "TMO" on that page and "TM" on Team.
 * The design's avatars are two letters (qe-oversight.html renders "SN"). */
export function initialsOf(name) {
  return (name || "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/** A timestamp as "14:32" today, or "9 Aug" on an earlier day.
 *
 * `todaySuffix` appends " today", which the design uses on the vehicle inspection
 * (vehicles.html seeds "06:48 today", and team.html matches) but not on the device
 * reading (devices.html seeds a bare "07:12"). Fleet was missing the suffix its own
 * design specifies while Team had it — same inspection, two renderings. */
export function formatDayTime(iso, { todaySuffix = false } = {}) {
  if (!iso) return null;
  const dt = new Date(iso);
  const isToday = dt.toDateString() === new Date().toDateString();
  if (!isToday) return dt.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const time = formatTimeOfDay(iso);
  return todaySuffix ? `${time} today` : time;
}

/** Time-of-day from a UTC timestamp, rendered in the viewer's own zone. The backend
 *  stores every instant in UTC by project mandate, so formatting is always the client's
 *  job — there are no bare TimeField strings left in this app.
 *
 *  24-hour explicitly, not by locale. The designs are 24-hour throughout — 83 distinct times
 *  and not one AM or PM among them, with values like 14:50 and 16:40 that a 12-hour clock
 *  cannot express — but the locale default renders "02:50 PM" on an en-US browser, which is
 *  what most of this team tests on. The zone stays the viewer's; only the notation is pinned. */
export function formatTimeOfDay(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** The alerts list's timestamp: "07:52 · 5 min ago".
 *
 *  Its own function rather than an option on relativeTime, because the design asks for a
 *  different elapsed format here. Records read "Updated 3 hr ago" — an approximation is all
 *  a last-touched label needs. An alert is a thing waiting to be dealt with, so the design
 *  keeps the minutes on the hours ("1h 38m ago", "5h 14m ago") and leads with the clock time,
 *  which is what someone reconciles against a radio call or an attendance sheet.
 *
 *  The clock half is deliberately the viewer's own zone, matching every other time in the app:
 *  the backend stores UTC and formatting is always the client's job.
 */
export function alertTimestamp(iso) {
  if (!iso) return null;
  const time = formatTimeOfDay(iso);
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

  let elapsed;
  if (diffMin < 1) {
    elapsed = "just now";
  } else if (diffMin < 60) {
    elapsed = `${diffMin} min ago`;
  } else if (diffMin < 1440) {
    const hr = Math.floor(diffMin / 60);
    const min = diffMin % 60;
    // "6h ago" rather than "6h 0m ago" on the exact hour — the design never shows a zero
    // minute component and it reads as a placeholder when it appears.
    elapsed = min ? `${hr}h ${min}m ago` : `${hr}h ago`;
  } else {
    const days = Math.floor(diffMin / 1440);
    elapsed = days === 1 ? "yesterday" : `${days} days ago`;
  }

  return `${time} · ${elapsed}`;
}

/** "Mon 15 Jun" — weekday, day, month, in that order.
 *
 *  Assembled from parts rather than taken from a locale pattern, for the same reason
 *  formatTimeOfDay pins the 24-hour clock: the designs are written day-first and an en-US
 *  browser renders "Wed, Aug 19". The month and weekday names still come from the viewer's
 *  locale; only the order is fixed.
 */
export function formatShortDay(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
  const month = date.toLocaleDateString(undefined, { month: "short" });
  return `${weekday} ${date.getDate()} ${month}`;
}

/** ExceptionHandler prefixes every error with "{timestamp}-{ViewName}-{method}-{email}: ".
 *  Fine on its own page, noise directly above a form field. */
export function stripErrorPrefix(message) {
  if (!message) return null;
  const idx = message.indexOf(": ");
  return idx === -1 ? message : message.slice(idx + 2);
}

/** "Updated 3 hr ago" for a record's last-touched timestamp. Generic date
 *  formatting that happened to live in the Sites page, which is why Site detail
 *  had to import from a sibling page to reuse it. */

export function relativeTime(isoString, { prefix = "Updated" } = {}) {
  // Alerts want the bare phrase ("3 hr ago"), records want it prefixed, so the prefix is a
  // parameter rather than a second near-identical function. Default keeps every existing
  // caller byte-identical.
  const lead = prefix ? `${prefix} ` : "";
  const diffMin = Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
  if (diffMin < 1) return `${lead}just now`;
  if (diffMin < 60) return `${lead}${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${lead}${diffHr} hr ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return `${lead}yesterday`;
  return `${lead}${diffDays} days ago`;
}
